import * as THREE from 'three'
import {
  defaultAnchorSpec,
  PendulumSim,
  PoseSmoother,
  quat,
  solveEarrings,
  type AnchorSpec,
  type CameraModel,
  type EarringsSolveResult,
  type FaceFrame,
  type ObservedEars,
  type POSE_SMOOTHER_PRESETS,
  type PoseSmootherConfig,
  type Quat,
} from '@ar-suite/core'
import { applyPose, ARScene, applyMaterialOverrides, disposeObject, FaceOccluder } from '@ar-suite/render-web'

export interface EarringsModuleOptions {
  spec?: AnchorSpec
  smoothing?: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS
}

interface Side {
  root: THREE.Group
  smoother: PoseSmoother
  pendulum: PendulumSim
  swing: Quat
}

/**
 * Earrings try-on module: both-lobe solver (core) → per-side smoother → three.js adapter.
 * Dangling earrings get the pendulum sim (camera-space gravity); studs follow the ear rigidly.
 */
export class EarringsModule {
  readonly left: Side
  readonly right: Side
  private _spec: AnchorSpec
  private model: THREE.Object3D | null = null
  private modelRight: THREE.Object3D | null = null
  private result: EarringsSolveResult | null = null
  private occluder: FaceOccluder | null = null
  private hangLength = 2.5

  constructor(readonly scene: ARScene, options: EarringsModuleOptions = {}) {
    this._spec = options.spec ?? defaultAnchorSpec('earring')
    const smoothing = options.smoothing ?? 'balanced'
    const mkSide = (name: string): Side => {
      const root = new THREE.Group()
      root.name = name
      root.visible = false
      this.scene.content.add(root)
      return { root, smoother: new PoseSmoother(smoothing), pendulum: new PendulumSim(), swing: [0, 0, 0, 1] }
    }
    this.left = mkSide('earring-left')
    this.right = mkSide('earring-right')
  }

  get spec(): AnchorSpec {
    return this._spec
  }
  get lastResult(): EarringsSolveResult | null {
    return this.result
  }
  get hasModel(): boolean {
    return this.model !== null
  }
  set visible(v: boolean) {
    this.left.root.visible = v
    this.right.root.visible = v && (this._spec.earring?.bothEars ?? true)
  }

  attachOccluder(occluder: FaceOccluder): void {
    this.occluder = occluder
    occluder.setSpec(this._spec.occluder)
  }

  setSpec(spec: AnchorSpec): void {
    this._spec = spec
    this.occluder?.setSpec(spec.occluder)
    this.configurePendulums()
  }

  patchSpec(patch: Partial<AnchorSpec>): void {
    Object.assign(this._spec, patch)
    this.setSpec(this._spec)
  }

  setSmoothing(cfg: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS): void {
    this.left.smoother.configure(cfg)
    this.right.smoother.configure(cfg)
  }

  /**
   * Replace the earring model (origin at the piercing, hanging along −Y, cm).
   * @param hangLengthCm pendulum length; defaults to the model's extent below the origin
   */
  setModel(model: THREE.Object3D | null, hangLengthCm?: number): void {
    if (this.model) {
      this.left.root.remove(this.model)
      disposeObject(this.model)
    }
    if (this.modelRight) {
      this.right.root.remove(this.modelRight)
      this.modelRight = null // shares geometry/materials with left — already disposed
    }
    this.model = model
    if (model) {
      this.left.root.add(model)
      this.modelRight = model.clone()
      this.right.root.add(this.modelRight)
      if (hangLengthCm) this.hangLength = hangLengthCm
      else {
        const box = new THREE.Box3().setFromObject(model)
        this.hangLength = Math.max(0.3, -box.min.y * 0.8)
      }
      this.configurePendulums()
    }
  }

  applyVariantMaterials(overrides: Record<string, { color?: string; metalness?: number; roughness?: number; opacity?: number }>): void {
    if (this.model) applyMaterialOverrides(this.model, overrides)
    if (this.modelRight) applyMaterialOverrides(this.modelRight, overrides)
  }

  update(frame: FaceFrame, cam: CameraModel, tMs: number, observed?: ObservedEars | null): EarringsSolveResult {
    this.result = solveEarrings(frame, cam, this._spec, observed, this.result ?? undefined)
    const dangle = this._spec.earring?.dangle ?? true
    const both = this._spec.earring?.bothEars ?? true
    this.updateSide(this.left, this.result.left.pose, dangle, tMs, true)
    this.updateSide(this.right, this.result.right.pose, dangle, tMs, both)
    return this.result
  }

  private updateSide(side: Side, pose: { position: [number, number, number]; rotation: Quat; scale: number }, dangle: boolean, tMs: number, visible: boolean): void {
    side.root.visible = visible
    if (!visible) return
    const smoothed = side.smoother.update(pose, tMs)
    if (dangle) {
      // position follows the (smoothed) lobe; orientation is gravity + swing, not the head
      side.pendulum.update(smoothed.position, tMs)
      side.pendulum.getSwing(side.swing)
      side.root.position.set(smoothed.position[0], smoothed.position[1], smoothed.position[2])
      side.root.quaternion.set(side.swing[0], side.swing[1], side.swing[2], side.swing[3])
      side.root.scale.setScalar(smoothed.scale)
    } else {
      applyPose(side.root, smoothed)
    }
  }

  onTrackingLost(): void {
    for (const side of [this.left, this.right]) {
      side.root.visible = false
      side.smoother.reset()
      side.pendulum.reset()
    }
  }

  private configurePendulums(): void {
    const L = this._spec.earring?.lengthCm ?? this.hangLength
    const damping = this._spec.earring?.damping
    for (const side of [this.left, this.right]) {
      side.pendulum.params.lengthCm = Math.max(0.3, L * this._spec.scale)
      if (damping !== undefined) side.pendulum.params.damping = damping
    }
  }

  dispose(): void {
    this.setModel(null)
    this.scene.content.remove(this.left.root, this.right.root)
  }
}
